<template>
  <div class="blog-page">
    <section class="blog-hero">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <p class="eyebrow">Blog</p>
            <h1 class="hero-title">Running a booked experience venue</h1>
            <p class="hero-sub">
              Product deep dives, operator playbooks, and what we are learning from the venues on the engine.
            </p>
          </div>
        </div>
      </div>
    </section>

    <div class="blog-body">
      <div class="container">
        <div class="row">
          <div v-for="post in posts" :key="post.slug" class="col-lg-6 d-flex">
            <router-link :to="'/blog/' + post.slug" class="post-card">
              <p class="post-card-eyebrow">{{ post.category }}</p>
              <h2 class="post-card-title">{{ post.title }}</h2>
              <p class="post-card-excerpt">{{ post.excerpt }}</p>
              <p class="post-card-meta">
                <span>{{ formatPostDate(post.date) }}</span>
                <span class="post-card-dot" aria-hidden="true">&middot;</span>
                <span>{{ post.readingMinutes }} min read</span>
                <span class="post-card-read">Read <svg-icon name="arrow-right" :size="14" /></span>
              </p>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { allPosts, formatPostDate } from '@/blog/posts';
import { applyHead, setJsonLd, clearJsonLd, SITE_URL } from '@/blog/head';

export default {
  name: 'BlogIndex',
  data() {
    return { posts: allPosts() };
  },
  mounted() {
    applyHead({
      title: 'Blog',
      description: 'Product deep dives and operator playbooks from Off The Couch, the Venue Engine for booked experience venues.',
      path: '/blog',
    });
    setJsonLd('blog', {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Off The Couch blog',
      url: SITE_URL + '/blog',
      blogPost: this.posts.map(p => ({
        '@type': 'BlogPosting',
        headline: p.title,
        datePublished: p.date,
        url: SITE_URL + '/blog/' + p.slug,
      })),
    });
  },
  beforeDestroy() {
    clearJsonLd(['blog']);
  },
  methods: { formatPostDate },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.blog-page { padding-top: 110px; padding-bottom: 80px; }

.blog-hero {
  position: relative;
  padding: 3rem 0 3.5rem;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -150px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 500px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.12), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .container { position: relative; z-index: 1; }
}

.hero-sub {
  color: $muted;
  font-size: 1.1rem;
  line-height: 1.6;
}

.post-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 1.75rem;
  padding: 2rem 2.25rem;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(225, 78, 202, 0.25);
    transform: translateY(-2px);
    text-decoration: none;
  }

  @media (max-width: 768px) { padding: 1.5rem; }
}

.post-card-eyebrow {
  color: $teal;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
}

.post-card-title {
  color: white;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 0.75rem;
}

.post-card-excerpt {
  color: $muted;
  font-size: 0.95rem;
  line-height: 1.6;
  flex: 1;
}

.post-card-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0 0;
  color: $muted-3;
  font-size: 0.82rem;
}

.post-card-dot { color: $muted-3; }

.post-card-read {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: $primary-text;
  font-weight: 600;
}

@media (max-width: 768px) {
  .blog-page { padding-top: 90px; }
  .blog-hero { padding: 2rem 0 2.5rem; }
  .hero-sub { font-size: 0.95rem; }
}
</style>
